import React, { useEffect } from 'react';
import { observer } from 'mobx-react-lite';
import { getSnapshot } from 'mobx-state-tree';
import { useTranslation } from 'react-i18next';
import ActivityListItem from '@/components/activity/ActivityListItem';
import { useActivityStore, useDomainStore, useNotificationPreferencesStore } from '@/hooks/useStores';
import type { ActivityEvent } from '@/types/activity';
import { formatRelativeTime } from '@/utils/formatRelativeTime';

interface OverviewRecentActivityCardProps {
  familyId: string;
  onViewAll: () => void;
}

const OverviewRecentActivityCard: React.FC<OverviewRecentActivityCardProps> = observer(({ familyId, onViewAll }) => {
  const { t } = useTranslation('overview');
  const activityStore = useActivityStore();
  const domainStore = useDomainStore();
  const notificationPrefsStore = useNotificationPreferencesStore();

  useEffect(() => {
    void activityStore.loadRecentFamily(familyId, { pageSize: 6 });
    void notificationPrefsStore.loadPreferences();
    return () => {
      activityStore.clearRecent();
    };
  }, [activityStore, notificationPrefsStore, familyId]);

  const items = activityStore.recentItems.map((item) => getSnapshot(item) as ActivityEvent);
  const isLoading = activityStore.isLoadingRecent;
  const latestCreatedAt = items[0]?.createdAt;
  const currentUserId = domainStore.user?.id;

  return (
    <section className="overview-card overview-activity-card" aria-labelledby="overview-recent-activity-title">
      <header className="overview-card__head">
        <h2 id="overview-recent-activity-title" className="overview-card__title">
          {t('activity.title')}
          {latestCreatedAt && <span className="overview-card__meta">{formatRelativeTime(latestCreatedAt)}</span>}
        </h2>
        <button type="button" className="overview-card__more" onClick={onViewAll}>
          {t('activity.viewAll')}
        </button>
      </header>
      {isLoading ? (
        <p className="overview-card__empty">{t('activity.loading')}</p>
      ) : items.length === 0 ? (
        <p className="overview-card__empty">{t('activity.empty')}</p>
      ) : (
        <div className="overview-card__body overview-activity-list">
          {items.map((event) => (
            <ActivityListItem
              key={event.id}
              event={event}
              isNew={notificationPrefsStore.isActivityNew(event.createdAt, 'global', event.actorUserId, currentUserId)}
            />
          ))}
        </div>
      )}
    </section>
  );
});

export default OverviewRecentActivityCard;
